import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Copy, Check, ExternalLink, Loader2, X, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export function ExportShareModal({ conversationId, onClose }) {
  const { token } = useAuth();
  const [shareUrl, setShareUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const generateLink = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/export/${conversationId}/share`,
        {},
        { headers }
      );
      const origin = window.location.origin;
      const url = `${origin}/shared-export/${data.share_id}`;
      setShareUrl(url);
      if (data.already_shared) {
        toast.info("This session already has a share link");
      } else {
        toast.success("Share link created!");
      }
    } catch {
      toast.error("Failed to generate share link");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — try selecting the link manually");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]" onClick={onClose} />
      <div
        data-testid="export-share-modal"
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[81] bg-card border border-border/50 rounded-3xl shadow-2xl w-[90%] max-w-md p-6 animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Link2 className="w-4.5 h-4.5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Share Session PDF</h3>
              <p className="text-[10px] text-muted-foreground">Create a link anyone can open</p>
            </div>
          </div>
          <button data-testid="export-share-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!shareUrl ? (
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-muted/30 border border-border/30">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium mb-1">What gets shared</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    A PDF of this session will be generated and made available via a permanent link.
                    Anyone with the link can view and download it — perfect for sharing with your therapist or coach.
                  </p>
                </div>
              </div>
            </div>
            <button
              data-testid="generate-share-link-btn"
              onClick={generateLink}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
              ) : (
                <><Link2 className="w-4 h-4" /> Generate Share Link</>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/40 border border-border/30">
              <input
                data-testid="share-link-input"
                readOnly
                value={shareUrl}
                className="flex-1 text-xs bg-transparent border-none outline-none text-foreground truncate"
                onClick={e => e.target.select()}
              />
              <button
                data-testid="copy-share-link-btn"
                onClick={copyLink}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  copied
                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                    : "bg-primary text-primary-foreground hover:opacity-90"
                )}
              >
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <div className="flex gap-2">
              <a
                href={shareUrl.replace("/shared-export/", "/api/share/export/")}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full border border-border/50 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Preview PDF
              </a>
              <button
                onClick={onClose}
                className="flex-1 py-2 rounded-full border border-border/50 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
              >
                Done
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center">
              This link never expires. Share it with anyone who needs to see this session.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
